import _ from 'underscore';
import PropTypes from 'prop-types';
import React, { useCallback, useMemo, useContext } from 'react';

import { PerformanceCard, PerformanceCardFluent } from '@bingads-webui-campaign-react/performance-card';
import { PerfMarkerFactory } from '@bingads-webui/performance-marker';
import { InstrumentationContext } from '@bingads-webui-react/hoc-with-instrumentation';
import { cardPerformanceId } from '@bingads-webui-campaign/fluent-page-consts';

import { useData } from './use-data';
import { getPreference } from './preference';

export const preferenceName = 'dashboard/preferences';
export const dashboardPreferenceKey = 'default-campaign-performancemax';
const defaultGetCardsConfig = () => [{ id: cardPerformanceId }];

const perfMarker = PerfMarkerFactory.createMarker('Overview_PerformanceCard');
perfMarker.willInit();

export const PerformanceCardContainer = (props) => {
  const {
    dateRange,
    filter,
    serverContext,
    perfMarkerUtils,
    sesId,
    i18n,
    preferenceService,
    selectedMetricIndexes,
    metrics,
    patchPreference,
    getCardsConfig,
    responsiveSize,
    shellContentElement,
    campaignType,
    constructOptions,
  } = props;
  const {
    CurrentAccount: {
      TimeZone,
    },
  } = serverContext;
  const instrumentation = useContext(InstrumentationContext);
  const { activity } = useMemo(() => instrumentation.createScenarioAndActivity({
    scenarioName: 'Fluent2OverviewPage',
    activityName: 'FluentPerformanceCard',
  }), [instrumentation]);
  const {
    willFetchData,
    willRender,
    done,
  } = perfMarkerUtils;

  const {
    initialSelectedMetricIndexesFromPreference,
    initialMetricsFromPreference,
    preferencePatch,
  } = useMemo(() => {
    // non pmax single campaign overview page is using new preference structure.
    if (selectedMetricIndexes && metrics && patchPreference) {
      return {
        initialSelectedMetricIndexesFromPreference: selectedMetricIndexes,
        initialMetricsFromPreference: metrics,
        preferencePatch: patchPreference,
      };
    }

    // in pmax single campaign overview page, we reuse the old preference structure from legacy overview page.
    // we may want to remove this after all the single campaign overview pages are migrated to new preference structure.
    const {
      get,
      set,
    } = getPreference({
      preferenceService,
      preferenceName,
      dashboardPreferenceKey,
      cardId: cardPerformanceId,
      activity,
    });
    const { selectedMetricIndexes: selectedMetricIndexesFromLegacy, metrics: metricsFromLegacy } = get();
    return {
      initialSelectedMetricIndexesFromPreference: selectedMetricIndexesFromLegacy,
      initialMetricsFromPreference: metricsFromLegacy,
      preferencePatch: set,
    };
  }, [activity, metrics, patchPreference, preferenceService, selectedMetricIndexes]);

  const {
    selectedMetricIndexes: selectedMetricIndexesFromDataHook,
    setSelectedMetricIndexes,
    visibleMetrics,
    setVisibleMetrics,
    performanceData,
    aggregatedMetricPickerData,
    hideCurrency,
    granularity,
    isLoading,
    isFirstDataLoaded,
    hasError,
  } = useData({
    dateRange,
    filter,
    i18n,
    initialSelectedMetricIndexesFromPreference,
    initialMetricsFromPreference,
    getCardsConfig,
    sesId,
    activity,
    willRender,
    willFetchData,
    campaignType,
    constructOptions,
  });

  const onMetricUpdate = useCallback((newSelectedMetricIndexes, newVisibleMetrics) => {
    setSelectedMetricIndexes(newSelectedMetricIndexes);
    setVisibleMetrics(newVisibleMetrics);

    preferencePatch({
      metrics: newVisibleMetrics,
      selectedMetricIndexes: newSelectedMetricIndexes,
    });
  }, [setSelectedMetricIndexes, setVisibleMetrics, preferencePatch]);

  if (isFirstDataLoaded) {
    done();
  }

  const Card = useMemo(() => (_.get(serverContext, ['Permissions', 'IsFluentPerformanceLinechartEnabled'], false) ? PerformanceCardFluent : PerformanceCard), [serverContext]);
  return (
    <Card
      selectedMetricIndexes={selectedMetricIndexesFromDataHook}
      visibleMetrics={visibleMetrics}
      aggregatedMetricPickerData={aggregatedMetricPickerData}
      granularity={granularity}
      hideCurrency={hideCurrency}
      TimeZone={TimeZone}
      performanceTimeSeries={performanceData}
      isComparisonEnabled={!!dateRange.compared}
      onMetricUpdate={onMetricUpdate}
      isLoading={isLoading}
      hasError={hasError}
      i18n={i18n}
      activity={activity}
      responsiveSize={responsiveSize}
      shellContentElement={shellContentElement}
    />
  );
};

PerformanceCardContainer.propTypes = {
  dateRange: PropTypes.shape({
    start: PropTypes.instanceOf(Date).isRequired,
    end: PropTypes.instanceOf(Date).isRequired,
    compared: PropTypes.shape({
      start: PropTypes.instanceOf(Date).isRequired,
      end: PropTypes.instanceOf(Date).isRequired,
    }),
  }).isRequired,
  filter: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  serverContext: PropTypes.shape({
    Urls: PropTypes.shape({
      ClientCenterBaseUrl: PropTypes.string.isRequired,
    }).isRequired,
    CurrentAccount: PropTypes.shape({
      TimeZone: PropTypes.number.isRequired,
    }),
  }).isRequired,
  i18n: PropTypes.shape({
    getString: PropTypes.func.isRequired,
  }).isRequired,
  sesId: PropTypes.number,
  preferenceService: PropTypes.shape({}),
  selectedMetricIndexes: PropTypes.arrayOf(PropTypes.number),
  metrics: PropTypes.arrayOf(PropTypes.string),
  patchPreference: PropTypes.func,
  getCardsConfig: PropTypes.func,
  responsiveSize: PropTypes.string.isRequired,
  perfMarkerUtils: PropTypes.shape({
    willFetchData: PropTypes.func.isRequired,
    willRender: PropTypes.func.isRequired,
    done: PropTypes.func.isRequired,
  }).isRequired,
  shellContentElement: PropTypes.instanceOf(Element),
  campaignType: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
  ]).isRequired,
};

PerformanceCardContainer.defaultProps = {
  sesId: -1,
  selectedMetricIndexes: null,
  metrics: null,
  patchPreference: null,
  getCardsConfig: defaultGetCardsConfig,
  shellContentElement: document.querySelector('body'),
  preferenceService: undefined,
};
